import { useEffect, useState } from "react";
import { useOptionalSideNavContext } from "./SideNavProvider";

/**
 * A `useState`-like boolean for row state inside a `SideNav` (an expanded
 * row) that survives the nav remounting under the same provider.
 * The value is kept under `id` by the nearest `SideNavProvider` for its
 * lifetime; without a provider it behaves like plain `useState(initialValue)`.
 * `id` must stay stable for the component's lifetime; key the component by
 * `id` otherwise.
 */
export const useSideNavMemory = ({
    id,
    initialValue,
}: {
    id: string;
    initialValue: boolean;
}): [boolean, (value: boolean) => void] => {
    const memoryRef = useOptionalSideNavContext()?.memoryRef;
    const [value, setValue] = useState(
        () => memoryRef?.current.get(id) ?? initialValue,
    );

    useEffect(() => {
        memoryRef?.current.set(id, value);
    }, [memoryRef, id, value]);

    return [value, setValue];
};
